import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import Table from "../../../../Components/v2/Table";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import CashbookWrapper from "../../../../Components/v2/Cashbook/CashbookWrapper";
import CashbookPageHeader from "../../../../Components/v2/Cashbook/CashbookPageHeader";
import CashbookTransactionModal from "../../../../Components/v2/Cashbook/CashbookTransactionModal";
import TableActions from "../../../../Components/v2/Table/TableActions";
import TableHeader from "../../../../Components/v2/Table/TableHeader";
import Pagination from "../../../../Components/v2/Pagination";
import StatsCard from "../../../../Components/v2/StatsCard";
import { ModalAlert } from "../../../../Components/ModalAlert";
import { ModalConfirm } from "../../../../Components/ModalConfirm";
import ExpandableDescription from "../../../../Components/v2/Accounting/ExpandableDescription";
import CashbookTransactionDetailModal from "../../../../Components/v2/Cashbook/CashbookTransactionDetailModal";
import {
  addLocalNotification,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
  requestListBusinessTransaction,
  requestDeleteCashTransaction,
  requestGetCashbookStats,
  requestUpdateBusinessTransaction,
  clearBusinessTransaction,
  clearBusinessTransactionError,
} from "../../../../Context";
import {
  cancelAPIRequest,
  currencyFormat,
  getErrorMessages,
} from "../../../../Utils";
import { PlusIcon, PencilIcon } from "@heroicons/react/outline";
import Modal from "../../../../Components/v2/Modal";
import FormInput from "../../../../Components/v2/Form/FormInput";
import Button from "../../../../Components/v2/Button";

const CashBook = () => {
  const { t, i18n } = useTranslation();

  const { user } = useAuthState();
  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();
  const {
    request,
    transactions,
    loading,
    error,
    nextTokens,
    deleteLoading,
    deletedId,
    submitError,
    submitLoading,
    updateTransaction,
    cashbookStats,
    cashbookStatsLoading,
    cashbookStatsError,
  } = useBusinessTransactionState();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [transactionID, setTransactionID] = useState("");
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isCashbookModalOpen, setIsCashbookModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditAmountModalOpen, setIsEditAmountModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState("");
  const [editingError, setEditingError] = useState(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearBusinessTransaction(dispatch);
      clearBusinessTransactionError(dispatch);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load transactions when business changes
  useEffect(() => {
    if (user.mainBusiness?.id) {
      requestListBusinessTransaction(dispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
      // Load cashbook stats
      const startDate = dayjs().startOf("month").toISOString();
      const endDate = dayjs().endOf("month").toISOString();
      requestGetCashbookStats(dispatch, {
        businessID: user.mainBusiness.id,
        startDate,
        endDate,
      });
    }
  }, [dispatch, user.mainBusiness?.id, perPage]);

  // Handle delete success
  useEffect(() => {
    if (!deletedId) {
      return;
    }
    setShowConfirmModal(false);
    clearBusinessTransaction(dispatch);
    // Show success notification
    addLocalNotification(notificationDispatch, {
      id: `delete-cashbook-${deletedId}`,
      type: "success",
      title: "common.success",
      body: "cashbook.notifications.deleteSuccess",
      showFull: true,
    });
  }, [deletedId, dispatch, notificationDispatch]);

  // Handle inline edit success
  useEffect(() => {
    if (updateTransaction?.id && isEditAmountModalOpen) {
      addLocalNotification(notificationDispatch, {
        id: `edit-cashbook-${updateTransaction.id}`,
        type: "success",
        title: "common.success",
        body: "cashbook.notifications.updateSuccess",
        showFull: true,
      });
      setEditingValue("");
      setEditingError(null);
      setIsEditAmountModalOpen(false);
      setSelectedTransaction(null);
      clearBusinessTransaction(dispatch);
    }
  }, [
    updateTransaction,
    notificationDispatch,
    dispatch,
    isEditAmountModalOpen,
  ]);

  // Handle submit error (only when Cashbook modal is NOT open — it handles errors inline)
  useEffect(() => {
    if (submitError && !isCashbookModalOpen) {
      setShowConfirmModal(false);
      setIsEditAmountModalOpen(false);
      setShowErrorModal(true);
    }
  }, [submitError, isCashbookModalOpen]);

  // Handle retry
  const handleRetry = () => {
    if (user.mainBusiness?.id) {
      clearBusinessTransactionError(dispatch);
      requestListBusinessTransaction(dispatch, {
        page: 1,
        limit: perPage,
        businessID: user.mainBusiness.id,
      });
    }
  };

  // Handle page change
  const onPageChange = (selectedPage) => {
    setPage(selectedPage);
    if (nextTokens[selectedPage] || selectedPage === 1) {
      requestListBusinessTransaction(dispatch, {
        page: selectedPage,
        limit: perPage,
        businessID: user.mainBusiness.id,
        nextToken: nextTokens[selectedPage],
      });
    }
  };

  // Handle per page change
  const onPerPageChange = (itemsPerPage) => {
    setPerPage(itemsPerPage);
    setPage(1);
  };

  // Handle cashbook modal close (create / full edit)
  const handleCashbookModalClose = (shouldRefresh) => {
    cancelAPIRequest(request);
    setIsCashbookModalOpen(false);
    setEditingTransaction(null);
    if (shouldRefresh) {
      handleReloadStats();
    }
  };

  // Handle view transaction
  const handleView = (transaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
  };

  // Handle edit transaction
  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setIsCashbookModalOpen(true);
  };

  // Handle inline edit (amount)
  const handleStartEdit = (transaction) => {
    const isIncome = transaction.income > 0;
    const amount = isIncome ? transaction.income : transaction.expense;
    setSelectedTransaction(transaction);
    setEditingValue(amount.toString());
    setEditingError(null);
    setIsEditAmountModalOpen(true);
  };

  const handleCancelEdit = () => {
    cancelAPIRequest(request);
    setSelectedTransaction(null);
    setEditingValue("");
    setEditingError(null);
    setIsEditAmountModalOpen(false);
  };

  const handleSaveEdit = () => {
    if (!editingValue || editingValue.trim() === "") {
      setEditingError("transactions.errors.amountRequired");
      return;
    }
    const newAmount = Number(editingValue.replace(/\D/g, ""));
    if (isNaN(newAmount) || newAmount <= 0) {
      setEditingError("accounting.transactions.modal.error.invalidAmount");
      return;
    }
    setEditingError(null);
    const isIncome = selectedTransaction.income > 0;
    requestUpdateBusinessTransaction(dispatch, {
      input: {
        id: selectedTransaction.id,
        type: "SALE",
        createdAt: selectedTransaction.createdAt,
        income: isIncome ? newAmount : 0,
        expense: isIncome ? 0 : newAmount,
        note: selectedTransaction.note || "",
        partnerID: selectedTransaction.partnerID || null,
      },
    });
  };

  // Handle delete transaction
  const handleDelete = (transaction) => {
    setTransactionID(transaction.id);
    setSelectedTransaction(transaction);
    setShowConfirmModal(true);
  };

  const getDeleteDescription = () => {
    if (!selectedTransaction) return null;
    const date = dayjs(selectedTransaction.createdAt)
      .locale(i18n.language)
      .format("D MMM YYYY");
    const isIncome = selectedTransaction.income > 0;
    const amountValue = isIncome
      ? selectedTransaction.income
      : selectedTransaction.expense;
    const amount = currencyFormat(amountValue);
    if (selectedTransaction.note) {
      return t("cashbook.modal.delete.descriptionItem", {
        note: selectedTransaction.note,
        amount: `${amount}`,
        date,
      });
    }
    return t("cashbook.modal.delete.descriptionItemNoNote", {
      amount: `${amount}`,
      date,
    });
  };

  // Confirm delete
  const onDelete = () => {
    requestDeleteCashTransaction(dispatch, {
      id: transactionID,
    });
  };

  // Reload cashbook stats
  const handleReloadStats = () => {
    if (user.mainBusiness?.id) {
      const startDate = dayjs().startOf("month").toISOString();
      const endDate = dayjs().endOf("month").toISOString();
      requestGetCashbookStats(dispatch, {
        businessID: user.mainBusiness.id,
        startDate,
        endDate,
      });
    }
  };

  // Skeleton loading component for stats
  const SkeletonStats = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6 animate-pulse"
          >
            <dt className="text-sm font-medium text-gray-500 truncate">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </dt>
            <dd className="mt-1 text-3xl font-semibold text-gray-900">
              <div className="h-8 bg-gray-300 rounded w-1/2"></div>
            </dd>
          </div>
        ))}
      </div>
    );
  };

  // Error state component for stats
  const ErrorStats = ({ error }) => {
    return (
      <div className="px-4 py-5 bg-red-100 border border-red-400 text-red-700 rounded-lg">
        <h3 className="font-medium">{t("common.errors.label")}</h3>
        <p>{t(getErrorMessages(error)) || "An error occurred."}</p>
        <Button onClick={handleReloadStats} className="mt-3" color="red">
          {t("common.refresh")}
        </Button>
      </div>
    );
  };

  // Total Financial Summary Component
  const TotalFinancial = () => {
    if (cashbookStatsLoading) {
      return <SkeletonStats />;
    }

    if (cashbookStatsError) {
      return <ErrorStats error={cashbookStatsError} />;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatsCard
          title={t("cashbook.stats.totalIncome")}
          value={cashbookStats?.total_income}
          showCurrency={true}
        />
        <StatsCard
          title={t("cashbook.stats.totalExpense")}
          value={cashbookStats?.total_expense}
          showCurrency={true}
        />
        <StatsCard
          title={t("cashbook.stats.balance")}
          value={cashbookStats?.balance}
          showCurrency={true}
        />
      </div>
    );
  };

  // Transform transactions for table display
  const transformedTransactions = transactions.map((transaction) => {
    const isIncome = transaction.income > 0;
    const amountValue = isIncome ? transaction.income : transaction.expense;

    const typeConfig = isIncome
      ? {
          label: "cashbook.type.sale",
          colorClass: "bg-green-100 text-green-800",
          textColorClass: "text-green-600",
          prefix: "+",
        }
      : {
          label: "cashbook.type.expense",
          colorClass: "bg-red-100 text-red-800",
          textColorClass: "text-red-600",
          prefix: "-",
        };

    return {
      id: transaction.id,
      date: transaction.createdAt,
      note: transaction.note || null,
      type: {
        label: typeConfig.label,
        colorClass: typeConfig.colorClass,
      },
      amount: {
        total: amountValue,
        value: currencyFormat(amountValue),
        colorClass: typeConfig.textColorClass,
        prefix: typeConfig.prefix,
      },
      originalTransaction: transaction,
    };
  });

  return (
    <CashbookWrapper>
      <main className="lg:col-span-9">
        {/* Header */}
        <CashbookPageHeader
          title="menu.cashbook"
          primaryAction={{
            label: t("cashbook.header.title.add"),
            icon: <PlusIcon className="h-5 w-5" />,
            onClick: () => {
              setEditingTransaction(null);
              setIsCashbookModalOpen(true);
            },
          }}
        />

        {/* Financial Summary */}
        <div className="mt-6 px-4 sm:px-6">
          <TotalFinancial />
        </div>

        {/* Recent Transactions */}
        <div className="mt-8 mb-4">
          <div className="px-4 sm:px-6 lg:px-8">
            <TableHeader
              title={t("cashbook.dashboard.transaction.header.title")}
              description={t(
                "cashbook.dashboard.transaction.header.description",
              )}
            />
          </div>
          <div className="mt-4 px-4 sm:px-6 lg:px-8">
            <Table
              isLoading={loading}
              error={error}
              onRetry={handleRetry}
              data={transformedTransactions}
              columns={[
                {
                  header: "cashbook.dashboard.transaction.field.date",
                  accessor: (transaction) => (
                    <DateTimeCell value={transaction.date} />
                  ),
                  sortable: true,
                  sortKey: "date",
                },
                {
                  header: "cashbook.dashboard.transaction.field.note",
                  accessor: (transaction) => {
                    const hasNote = !!transaction.note;
                    return (
                      <div className="max-w-[200px] lg:max-w-xs whitespace-normal break-words">
                        {hasNote ? (
                          <ExpandableDescription text={transaction.note} />
                        ) : (
                          <span className="text-gray-400 italic">
                            {t("cashbook.dashboard.transaction.noNote")}
                          </span>
                        )}
                      </div>
                    );
                  },
                },
                {
                  header: "cashbook.dashboard.transaction.field.type",
                  accessor: (transaction) => (
                    <span
                      className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${transaction.type.colorClass}`}
                    >
                      {t(transaction.type.label)}
                    </span>
                  ),
                },
                {
                  header: "cashbook.dashboard.transaction.field.amount",
                  accessor: (transaction, index) => {
                    const originalTransaction = transactions[index];
                    return (
                      <div
                        className="flex items-center justify-end px-2 py-1 rounded-md transition-colors duration-200 -mr-2 cursor-pointer group hover:bg-gray-100"
                        onClick={() => handleStartEdit(originalTransaction)}
                        role="button"
                        title={t("cashbook.dashboard.transaction.clickToEdit")}
                      >
                        <span
                          className={`${transaction.amount.colorClass} font-medium`}
                        >
                          {transaction.amount.prefix}
                          {transaction.amount.value}
                        </span>
                        <div className="w-3.5 h-3.5 ml-2 flex-shrink-0">
                          <PencilIcon
                            className="w-full h-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    );
                  },
                  align: "right",
                },
                {
                  header: "common.actions.label",
                  accessor: (transaction, index) => (
                    <TableActions
                      position={
                        index === transformedTransactions.length - 1
                          ? "top"
                          : "bottom"
                      }
                      actions={[
                        {
                          label: t("common.view"),
                          onClick: () =>
                            handleView(transaction.originalTransaction),
                        },
                        {
                          label: t("common.edit"),
                          onClick: () =>
                            handleEdit(transaction.originalTransaction),
                        },
                        {
                          label: t("common.delete"),
                          onClick: () =>
                            handleDelete(transaction.originalTransaction),
                          variant: "danger",
                        },
                      ]}
                    />
                  ),
                  align: "right",
                },
              ]}
            />
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          nextTokens={nextTokens}
          currentPage={page}
          isLoading={loading}
          perPage={perPage}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
      </main>

      {/* Cashbook Transaction Modal (create / edit) */}
      {isCashbookModalOpen && (
        <CashbookTransactionModal
          open={isCashbookModalOpen}
          onClose={handleCashbookModalClose}
          transaction={editingTransaction}
        />
      )}

      {/* Transaction Detail Modal */}
      {isDetailModalOpen && (
        <CashbookTransactionDetailModal
          open={isDetailModalOpen}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedTransaction(null);
          }}
          onEdit={(transaction) => {
            setIsDetailModalOpen(false);
            handleEdit(transaction);
          }}
          transaction={selectedTransaction}
        />
      )}

      {/* Edit Amount Modal */}
      <Modal
        open={isEditAmountModalOpen}
        onClose={handleCancelEdit}
        title="cashbook.dashboard.transaction.clickToEdit"
        footer={
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={handleCancelEdit}
              variant="secondary"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSaveEdit}
              disabled={submitLoading}
              variant="primary"
            >
              {t("common.save")}
            </Button>
          </div>
        }
      >
        <FormInput
          label={t("transactions.fields.amount")}
          value={editingValue}
          onChange={(e) => {
            setEditingValue(e.target.value);
            if (editingError) setEditingError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !submitLoading) {
              handleSaveEdit();
            }
          }}
          numberFormat={{
            thousandSeparator: true,
          }}
          prefix="Rp"
          error={editingError}
          disabled={submitLoading}
          onClear={() => setEditingValue("")}
          autoFocus
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <ModalAlert
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        title="cashbook.modal.delete.title"
        description="cashbook.modal.delete.description"
        descriptionNode={getDeleteDescription()}
        confirmButtonText="common.yes"
        cancelButtonText="common.cancel"
        loadingButton={deleteLoading}
        disabledButton={deleteLoading}
        onConfirm={onDelete}
        onCancel={() => {
          setShowConfirmModal(false);
          setSelectedTransaction(null);
        }}
      />

      {/* Error Modal */}
      {submitError && (
        <ModalConfirm
          open={showErrorModal}
          type="ALERT"
          setOpen={setShowErrorModal}
          title="Error"
          description={getErrorMessages(submitError)}
          onConfirm={() => {
            setShowErrorModal(false);
            clearBusinessTransactionError(dispatch);
          }}
        />
      )}
    </CashbookWrapper>
  );
};

export default CashBook;
